// The document filters of a document list that has no catalog toolbar of its own - a system's
// Dokumente tab, an article's Dokumente tab, the Merkliste. Same controls as the catalog's
// document mode: a filter icon opening the drawer with DocumentFacetsPanel (counts, smart graying),
// and the set filters as removable chips under it. These lists used to carry their own inline
// Sprache/Dokumentart selects instead - a second way of doing the same thing, whose choices showed
// up nowhere else (review point 7: "überall dieselbe Bedienung, Badges auch dort").

import { ActionIcon, Drawer, Group } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo, type ReactNode } from 'react';

import { computeDocumentFacets, humanizeDocType, languageLabel, type TreeIndex } from '../engine';
import { useCatalogLabels } from '../labels';
import { ActiveFilterChips, type FilterChip } from './ActiveFilterChips';
import { DocumentFacetsPanel } from './DocumentFacetsPanel';
import { FILTER_DRAWER_HEADER_PROPS, FilterDrawerHeader } from './FilterDrawerHeader';

import type { DocumentIndexItem, IndexItem } from '../types';

interface DocumentFilterBarProps {
    /** The documents of THIS list - the facets offer what occurs here, counted against the other
     * filters (computeDocumentFacets), exactly like the catalog's document drawer. */
    documents: DocumentIndexItem[];
    indexByArtNr: Map<string, IndexItem>;
    treeIndex: TreeIndex;
    /** The list's own text search, if it has one - the facets count within its hits. */
    search?: string;
    language: string | null;
    docTypes: string[];
    onLanguageChange: (value: string | null) => void;
    onDocTypesChange: (value: string[]) => void;
    /** Rendered in the same row, left of the filter icon - typically the list's search field. */
    leading?: ReactNode;
    /** Under the drawer heading: what the filters act on (see FilterDrawerHeader's `context`). */
    context?: ReactNode;
    /** Caption of the chip row ("Filter in Träger XT20:"). */
    chipsLabel?: string;
    /** Above the article overlay (DetailOverlay) when the bar lives inside it. */
    zIndex?: number;
}

const SEARCH_MIN_CHARS = 2;

export const DocumentFilterBar = ({
    documents,
    indexByArtNr,
    treeIndex,
    search = '',
    language,
    docTypes,
    onLanguageChange,
    onDocTypesChange,
    leading,
    context,
    chipsLabel,
    zIndex
}: DocumentFilterBarProps) => {
    const { L, locale } = useCatalogLabels();
    const [opened, { open, close }] = useDisclosure(false);

    const facets = useMemo(
        () =>
            computeDocumentFacets(
                documents,
                indexByArtNr,
                treeIndex,
                SEARCH_MIN_CHARS,
                { search, language: language ?? undefined, docTypes, page: 1, perPage: 1 },
                locale
            ),
        [documents, indexByArtNr, treeIndex, search, language, docTypes, locale]
    );
    const hasFacets = facets.languages.length > 1 || facets.docTypes.length > 1;
    const active = Boolean(language) || docTypes.length > 0;

    const chips: FilterChip[] = [
        ...(language
            ? [
                  {
                      key: 'language',
                      group: L.documentLanguage,
                      value: languageLabel(language, locale),
                      onRemove: () => onLanguageChange(null)
                  }
              ]
            : []),
        ...docTypes.map((docType) => ({
            key: `docType:${docType}`,
            group: L.documentCategory,
            value: humanizeDocType(docType),
            onRemove: () => onDocTypesChange(docTypes.filter((value) => value !== docType))
        }))
    ];

    return (
        <>
            <Group gap="sm" wrap="nowrap">
                {leading && <div style={{ flex: 1, minWidth: 0 }}>{leading}</div>}
                {/* Shown while a filter is set even if the list no longer varies, so the way back
                    out stays reachable. */}
                {(hasFacets || active) && (
                    <ActionIcon
                        aria-label={L.documentFilters}
                        aria-pressed={active}
                        size="lg"
                        title={L.documentFilters}
                        variant={active ? 'filled' : 'default'}
                        onClick={open}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                            tune
                        </span>
                    </ActionIcon>
                )}
            </Group>
            <ActiveFilterChips
                L={L}
                chips={chips}
                label={chipsLabel}
                onClearAll={() => {
                    onLanguageChange(null);
                    onDocTypesChange([]);
                }}
            />
            <Drawer
                opened={opened}
                padding="lg"
                position="right"
                title={L.documentFilters}
                zIndex={zIndex}
                onClose={close}
                {...FILTER_DRAWER_HEADER_PROPS}
                // App.css pins every drawer's inner layer to z-index 1000 - below the article
                // overlay this bar can live in. Inline beats that class rule.
                styles={{ ...FILTER_DRAWER_HEADER_PROPS.styles, inner: zIndex ? { zIndex } : {} }}
            >
                <FilterDrawerHeader
                    canReset={active}
                    context={context}
                    title={L.documentFilters}
                    onClose={close}
                    onReset={() => {
                        onLanguageChange(null);
                        onDocTypesChange([]);
                    }}
                />
                <DocumentFacetsPanel
                    docTypes={facets.docTypes}
                    editions={[]}
                    language={language}
                    languages={facets.languages}
                    locale={locale}
                    selectedDocTypes={docTypes}
                    selectedEditions={[]}
                    onDocTypesChange={onDocTypesChange}
                    onEditionsChange={() => undefined}
                    onLanguageChange={onLanguageChange}
                />
            </Drawer>
        </>
    );
};
